import {CognitoUser} from 'amazon-cognito-identity-js'

import {Dealer} from '@/types/dynamodb-types'
import {Branch, CognitoGroups} from '@/utils/constants'

export interface CanSee {
  canSee: boolean
}

export interface CanCreate {
  canCreate: boolean
}

export interface CanManage {
  canManage: boolean
}

export interface CanEdit {
  canEdit: boolean
}

export interface CanDelete {
  canDelete: boolean
}

export interface Permissions {
  leads: CanSee
  offers: CanSee
  registrations: CanSee
  registrationArchivedFilters: CanSee
  customers: CanSee
  appointments: CanSee
  salesAppointments: CanSee & CanCreate & {canEdit: boolean; canDelete: boolean}
  dealerBikes: CanSee & CanCreate
  fleetManagement: CanSee
  userCredentials: CanSee
  cognitoUserManagement: CanSee
  stocks: CanSee & CanManage
  imports: CanSee
  dealerProfile: CanSee
  dealerDashboard: CanSee
  sectionLoansSales: CanSee
  requests: CanSee
  help: CanSee
  supportsSubagent: boolean
  clientHistory: CanSee
}

export class PermissionsHelper {
  private readonly cognitoUser: CognitoUser
  private readonly dealer: Dealer

  public constructor(cognitoUser: CognitoUser, dealer: Dealer) {
    this.cognitoUser = cognitoUser
    this.dealer = dealer
  }

  public calculatePermissions(): Permissions {
    return {
      leads: {
        canSee:
          this.allowedByRole([CognitoGroups.DealerAdmin, CognitoGroups.SalesAgent]) &&
          this.allowedByBranch([Branch.HondaSpain, Branch.HondaPortugal, Branch.HondaUSA]),
      },
      offers: {
        canSee:
          this.allowedByRole([CognitoGroups.DealerAdmin, CognitoGroups.SalesAgent]) &&
          this.allowedByBranch([Branch.HondaSpain, Branch.HondaPortugal, Branch.HondaUSA]),
      },
      registrations: {
        canSee:
          this.allowedByRole([CognitoGroups.DealerAdmin, CognitoGroups.SalesAgent]) &&
          this.allowedByBranch([Branch.HondaSpain, Branch.HondaPortugal, Branch.HondaUSA]),
      },
      registrationArchivedFilters: {
        canSee:
          this.allowedByRole([CognitoGroups.DealerAdmin]) &&
          this.allowedByBranch([Branch.HondaSpain, Branch.HondaPortugal, Branch.HondaUSA]),
      },
      customers: {
        canSee:
          this.allowedByRole([CognitoGroups.DealerAdmin, CognitoGroups.SalesAgent]) &&
          this.allowedByBranch([Branch.HondaSpain, Branch.HondaPortugal, Branch.HondaUSA]),
      },
      appointments: {
        canSee: this.allowedByRole([CognitoGroups.DealerAdmin, CognitoGroups.SalesAgent, CognitoGroups.GarageManager]),
      },
      salesAppointments: {
        canSee: this.allowedByRole([CognitoGroups.DealerAdmin, CognitoGroups.SalesAgent]),
        canCreate: this.allowedByRole([CognitoGroups.DealerAdmin, CognitoGroups.SalesAgent]),
        canEdit: this.allowedByRole([CognitoGroups.DealerAdmin, CognitoGroups.SalesAgent]),
        canDelete: this.allowedByRole([CognitoGroups.DealerAdmin, CognitoGroups.SalesAgent]),
      },
      dealerBikes: {
        canSee: this.allowedByBranch([Branch.HondaSpain, Branch.HondaPortugal, Branch.HondaUSA]),
        canCreate: this.allowedByRole([CognitoGroups.DealerAdmin]),
      },
      fleetManagement: {
        canSee: this.allowedByRole([CognitoGroups.DealerAdmin]),
      },
      userCredentials: {
        canSee:
          this.allowedByRole([CognitoGroups.DealerAdmin]) &&
          this.allowedByBranch([Branch.HondaSpain, Branch.HondaPortugal, Branch.HondaUSA]),
      },
      cognitoUserManagement: {
        canSee: this.allowedByRole([CognitoGroups.DealerAdmin]) && this.isAllowedForCognitoUserManagement(),
      },
      stocks: {
        canSee:
          this.allowedByRole([CognitoGroups.StockAdmin, CognitoGroups.DealerAdmin], [CognitoGroups.ServicePartner]) &&
          this.allowedByBranch([Branch.HondaSpain, Branch.HondaPortugal, Branch.HondaUSA]),
        canManage: this.allowedByRole([CognitoGroups.StockAdmin]),
      },
      imports: {
        canSee: this.allowedByRole([CognitoGroups.Imports]),
      },
      requests: {
        canSee:
          this.allowedByRole([CognitoGroups.DealerAdmin, CognitoGroups.GarageManager, CognitoGroups.SalesAgent]) &&
          this.allowedByBranch([Branch.HondaSpain, Branch.HondaPortugal, Branch.HondaUSA]),
      },
      dealerProfile: {
        canSee: this.allowedByRole([CognitoGroups.DealerAdmin]),
      },
      dealerDashboard: {
        canSee:
          this.allowedByRole([CognitoGroups.DealerAdmin, CognitoGroups.SalesAgent]) &&
          this.allowedByBranch([Branch.HondaSpain, Branch.HondaPortugal, Branch.HondaUSA]),
      },
      sectionLoansSales: {
        canSee:
          this.allowedByRole([CognitoGroups.DealerAdmin, CognitoGroups.SalesAgent]) &&
          this.allowedByBranch([Branch.HondaSpain, Branch.HondaPortugal, Branch.HondaUSA]),
      },
      help: {
        canSee: this.allowedByRole([CognitoGroups.DealerAdmin, CognitoGroups.GarageManager, CognitoGroups.SalesAgent]),
      },
      supportsSubagent: this.allowedByBranch([Branch.HondaSpain]),
      clientHistory: {
        canSee:
          this.allowedByRole([CognitoGroups.DealerAdmin, CognitoGroups.SalesAgent]) &&
          this.allowedByBranch([Branch.HondaSpain, Branch.HondaPortugal, Branch.HondaUSA]),
      },
    }
  }

  private allowedByBranch(allowedBranches: Branch[]) {
    return allowedBranches.some((c: Branch) => String(this.dealer.country) === c)
  }

  private isAllowedForCognitoUserManagement(): boolean {
    // PanEX users are managed externally via Honda SSO, they should NEVER see Cognito User Management
    // Note: cognitoUser is a compatible user object from user-compat.ts that includes attributes
    const isPanexUser = !!(this.cognitoUser as any)?.attributes?.panex_id
    if (isPanexUser) {
      return false
    }

    // Only Cognito-native users from Muvio/Mapit dealers can manage users
    const isProduction = process.env.VUE_APP_ENV_MODE === 'production'
    const allowedDealerIds = isProduction
      ? [
          'de-2GiovX0kyTJ98wehjeNR484L63a', // Muvio PROD
          'de-1gMkpX60OvPmqPz1483LNt0BGPf', // Mapit Group PROD
        ]
      : [
          'de-2GioPkVHrreZThdEzyCjj4gJ5ag', // Muvio DEV
          'de-1gMfari1f5BjpzlsO0OKyThOVVV', // Mapit Group DEV
        ]
    return allowedDealerIds.includes(this.dealer?.id)
  }

  private allowedByRole(allowedRoles: CognitoGroups[], forbiddenRoles?: CognitoGroups[]) {
    const groups = this.cognitoUser?.getSignInUserSession()?.getAccessToken().decodePayload()['cognito:groups'] || []

    if (forbiddenRoles?.some((g: CognitoGroups) => groups.includes(g))) {
      return false
    }

    return [...allowedRoles, CognitoGroups.Admin].some((g: CognitoGroups) => groups.includes(g))
  }
}
